import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react/hooks';
import { Printer } from '@oolio-group/domain';
import { GET_PRINTERS_QUERY } from '../graphql/printers';
import { noopHandler } from '../utils/general';
import { createNameComparator, sortByName } from '../utils/sorting';
import { useSession } from './useSession';

/**
 * Exactly the fields `GET_PRINTERS_QUERY` selects. The KDS only needs enough
 * of a printer to list it and hand its id to the printing path, so the query
 * deliberately omits the rest of `Printer` — including required fields such as
 * `isKOT`, `store` and `isActive`. Modelling that here keeps the hook from
 * claiming to return objects it never receives.
 */
export type KdsPrinter = Pick<
  Printer,
  | 'id'
  | 'name'
  | 'code'
  | 'brand'
  | 'ipAddress'
  | 'slug'
  | 'bdAddress'
  | 'series'
  | 'printerType'
>;

export interface UsePrintersProps {
  loading: boolean;
  printers: KdsPrinter[];
}

export function usePrinters(): UsePrintersProps {
  const { session } = useSession();
  const { data, loading } = useQuery<{ printers: KdsPrinter[] }>(
    GET_PRINTERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      onError: noopHandler,
    },
  );

  const compareNames = useMemo(
    () => createNameComparator(session?.localeCode),
    [session?.localeCode],
  );

  // Sorted here rather than at the call site so every consumer gets a stable
  // order without having to remember; the server returns them unordered.
  // Kept memoised so consumers see a stable list identity between renders,
  // exactly as the previous `useState`-backed implementation did.
  const printers = useMemo<KdsPrinter[]>(
    () => sortByName(data?.printers ?? [], compareNames),
    [data, compareNames],
  );

  return useMemo(() => ({ loading, printers }), [loading, printers]);
}
